import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { fetchSiteOwnerCourts } from "../../store/CourtSlice";
import { useLivestreamForm } from "../../hooks/useLivestreamForm";
import { useLivestreamManagement } from "../../hooks/useLivestreamManagement";
import BasicInformationSection from "./BasicInformationSection";
import ScheduleSection from "./ScheduleSection";
import EventDetailsSection from "./EventDetailsSection";
import SponsorManagement from "./SponsorManagement";
import { toast } from 'react-hot-toast';
import styled from 'styled-components';

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.75rem 1.5rem;
  background: #BD2026;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #010101;
    transform: translateY(-1px);
    box-shadow: 0 6px 8px -1px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(189, 32, 38, 0.2);
  }
`;

const Container = styled.div`
  min-height: 100vh;
  background: #f9f9f9;
  padding: 1.5rem 0;
`;

const MainContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  background: #010101;
  padding: 1.5rem 2rem;
  color: white;
`;

const Title = styled.h2`
  font-size: 1.875rem;
  font-weight: 700;
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0.5rem 0 0 0;
  opacity: 0.8;
`;

const Content = styled.div`
  padding: 1.5rem 2rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  color: #dc2626;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.div`
  padding: 1rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 0.5rem;
  color: #16a34a;
  margin-bottom: 1rem;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
`;

const Spinner = styled.div`
  width: 3rem;
  height: 3rem;
  border: 3px solid #f3f4f6;
  border-top: 3px solid #010101;
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

const LoadingText = styled.p`
  margin-left: 1rem;
  color: #6b7280;
`;

export default function ScheduleLivestream() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { courts = [], loading: courtsLoading, error: courtsError } = useSelector((state) => state.court || {});
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [scheduleSuccess, setScheduleSuccess] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);

  // Custom hooks
  const {
    formState,
    setters,
    isFormValid,
    handleAddSponsor,
    handleRemoveSponsor,
    handleLogoUpload,
    handleCameraChange,
    prepareLivestreamData,
    calculateDuration,
    resetForm
  } = useLivestreamForm();

  const {
    handleCreateLivestream,
    handleScheduleLivestream,
    isScheduling
  } = useLivestreamManagement(null, user);

  // Calculate duration and detect midnight crossing
  const duration = calculateDuration();
  const crossesMidnight = React.useMemo(() => {
    if (!formState.date || !formState.startTime || !formState.endTime) return false;
    
    // Create Date objects with proper timezone handling
    const startDateTime = new Date(`${formState.date}T${formState.startTime}:00`);
    const endDateTime = new Date(`${formState.date}T${formState.endTime}:00`);
    
    // If end time is earlier than start time in 24-hour format, it crosses midnight
    // Examples: 23:00 to 01:00, 22:30 to 02:15, etc.
    return endDateTime < startDateTime;
  }, [formState.date, formState.startTime, formState.endTime]);

  // Extract time-related validation errors
  const timeValidationErrors = React.useMemo(() => {
    if (!isFormValid.valid) {
      return isFormValid.errors.filter(error => 
        error.includes('duration') || 
        error.includes('time') ||
        error.includes('15 minutes') ||
        error.includes('24 hours')
      );
    }
    return [];
  }, [isFormValid]);

  // Available cameras based on selected court
  const availableCameras = useMemo(
    () => {
      const selectedCourt = courts.find(c => c.id === Number(formState.selectedCourtId));
      return selectedCourt?.cameras || [];
    },
    [courts, formState.selectedCourtId]
  );

  // Fetch courts on component mount
  useEffect(() => {
    dispatch(fetchSiteOwnerCourts());
  }, [dispatch]);

  // Enhanced form submission with backend-aligned scheduling
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Enhanced scheduling flow triggered');
    
    try {
      setScheduleError(null);
      setScheduleSuccess(null);
      setPaymentUrl(null);

      console.log('Form validation:', isFormValid);
      console.log('Current form state:', formState);
      if (!isFormValid.valid) {
        console.log('Form validation failed:', isFormValid.errors);
        toast.error(`Please fill out all required fields: ${isFormValid.errors.join(', ')}`);
        return;
      }

      // Use enhanced scheduling flow that aligns with backend architecture
      const savedStream = await handleScheduleLivestream(formState, courts, (livestream, options) => {
        console.log('Livestream scheduling completed:', livestream);
        
        if (options?.paymentRequired) {
          setScheduleSuccess("Livestream scheduled! Owner payment required to activate streaming.");
          setPaymentUrl(options.paymentUrl);
        } else {
          setScheduleSuccess("Livestream scheduled successfully!");
        }
        
        setShowModal(true);
        resetForm();
      });

    } catch (err) {
      console.error('Scheduling error:', err);
      setScheduleError(err.message || "Failed to schedule livestream. Please try again.");
      setShowModal(true);
    }
  };

  // Handle court change
  const handleCourtChange = (courtId) => {
    setters.setSelectedCourtId(courtId);
    setters.setSelectedCamera('');
    setters.setSelectedCameraId('');
  };

  // Handle camera change
  const handleCameraChangeWrapper = (e) => {
    handleCameraChange(e, availableCameras);
  };

  if (courtsLoading) {
    return (
      <Container>
        <MainContainer>
          <LoadingSpinner>
            <Spinner />
            <LoadingText>Loading courts...</LoadingText>
          </LoadingSpinner>
        </MainContainer>
      </Container>
    );
  }

  return (
    <Container>
      <MainContainer>
        <Card>
          <Header>
            <Title>Schedule Livestream</Title>
            <Subtitle>Create a new livestream event for your court</Subtitle>
          </Header>

          <Content>
            {courtsError && (
              <ErrorMessage>{courtsError}</ErrorMessage>
            )}
            {scheduleError && (
              <ErrorMessage>{scheduleError}</ErrorMessage>
            )}
            {scheduleSuccess && (
              <SuccessMessage>{scheduleSuccess}</SuccessMessage>
            )}

            {/* Time validation errors */}
            {timeValidationErrors.length > 0 && (
              <ErrorMessage>
                <strong>Schedule Issues:</strong>
                <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0 }}>
                  {timeValidationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </ErrorMessage>
            )}

            <form onSubmit={handleSubmit}>
              <Grid>
                <LeftColumn>
                  <BasicInformationSection
                    livestreamName={formState.livestreamName}
                    selectedCourtId={formState.selectedCourtId}
                    selectedCamera={formState.selectedCamera}
                    selectedCameraId={formState.selectedCameraId ? String(formState.selectedCameraId) : ''}
                    availableCameras={availableCameras}
                    courts={courts}
                    onLivestreamNameChange={setters.setLivestreamName}
                    onCourtChange={handleCourtChange}
                    onCameraChange={handleCameraChangeWrapper}
                    gameType={formState.gameType}
                    onGameTypeChange={setters.setGameType}
                  />

                  <ScheduleSection
                    date={formState.date}
                    startTime={formState.startTime}
                    endTime={formState.endTime}
                    duration={duration}
                    crossesMidnight={crossesMidnight}
                    onDateChange={setters.setDate}
                    onStartTimeChange={setters.setStartTime}
                    onEndTimeChange={setters.setEndTime}
                  />
                </LeftColumn>

                <RightColumn>
                  <EventDetailsSection
                    isPublic={formState.isPublic}
                    onVisibilityChange={setters.setIsPublic}
                    discountCode={formState.discountCode}
                    onDiscountCodeChange={setters.setDiscountCode}
                    viewersPrice={formState.viewersPrice}
                    onViewersPriceChange={setters.setViewersPrice}
                    teamAName={formState.teamAName}
                    teamBName={formState.teamBName}
                    onTeamANameChange={setters.setTeamAName}
                    onTeamBNameChange={setters.setTeamBName}
                  />

                  <SponsorManagement
                    sponsors={formState.sponsors}
                    tempSponsorName={formState.tempSponsorName}
                    onAddSponsor={handleAddSponsor}
                    onRemoveSponsor={handleRemoveSponsor}
                    onLogoUpload={handleLogoUpload}
                    onTempSponsorNameChange={setters.setTempSponsorName}
                  />
                </RightColumn>
              </Grid>

              <SubmitButton type="submit" disabled={isScheduling}>
                {isScheduling ? 'Scheduling Livestream...' : 'Schedule Livestream'}
              </SubmitButton>
            </form>
          </Content>
        </Card>
      </MainContainer>

      {/* Success Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton className="bg-[#010101] text-white">
          <Modal.Title className="text-xl font-semibold">Success</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white p-6">
          <p className="text-[#333333] mb-4">{scheduleSuccess}</p>
          {paymentUrl && (
            <a
              href={paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full px-6 py-3 bg-[#BD2026] text-white rounded-lg hover:bg-[#010101] transition-colors focus:ring-2 focus:ring-[#BD2026]/20 focus:outline-none text-center font-semibold"
            >
              Pay now
            </a>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}
